// src/AuthContext.js
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";

const AuthContext = createContext();

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
};

/* ========= Storage Keys ========= */
const STORAGE_USER_KEY = "smh_user_v2";
const STORAGE_TOKEN_KEY = "smh_token_v2";

/* ========= API Base ========= */
const RAW_ENV_BASE = process.env.REACT_APP_API_BASE || "/api/ords/schools/";
const API_BASE = RAW_ENV_BASE.replace(/\/+$/, "") + "/";

const joinPath = (base, path = "") =>
  base.replace(/\/+$/, "") + "/" + String(path || "").replace(/^\/+/, "");

export const buildApiUrl = (path = "", params = {}) => {
  const clean = String(path || "").replace(/^\/+/, "");
  const base = API_BASE;

  if (/^https?:\/\//i.test(base)) {
    const url = new URL(joinPath(base, clean));
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "")
        url.searchParams.set(k, String(v));
    });
    return url.toString();
  }

  const urlStr = joinPath(base, clean);
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") usp.set(k, String(v));
  });
  const qs = usp.toString();
  return qs ? `${urlStr}?${qs}` : urlStr;
};

/* ===== Oracle Error Mapping ===== */
const mapOracleError = (text = "") => {
  const t = String(text);
  if (/ORDS-25001/i.test(t)) return "Server error in REST handler.";
  if (/ORA-00001/i.test(t)) return "Value already exists.";
  if (/ORA-01400/i.test(t)) return "Missing required value.";
  if (/ORA-12899/i.test(t)) return "Value too long.";
  if (/ORA-06502/i.test(t)) return "Numeric or value error.";
  if (/ORA-00904|invalid identifier/i.test(t)) return "Invalid identifier.";
  if (/ORA-00907|ORA-00933|ORA-00936/i.test(t)) return "SQL syntax error.";
  if (/ORA-01036/i.test(t)) return "Bind variable mismatch.";
  return null;
};

const parseMaybeJson = async (res) => {
  const raw = await res.text();
  try {
    return { json: JSON.parse(raw), raw };
  } catch {
    return { json: null, raw };
  }
};

const initUserFromStorage = () => {
  try {
    const value = localStorage.getItem(STORAGE_USER_KEY);
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
};

const initTokenFromStorage = () => {
  try {
    return localStorage.getItem(STORAGE_TOKEN_KEY) || null;
  } catch {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(initUserFromStorage);
  const [token, setToken] = useState(initTokenFromStorage);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  const save = useCallback((k, v) => {
    try {
      localStorage.setItem(k, v);
    } catch {}
  }, []);

  const del = useCallback((k) => {
    try {
      localStorage.removeItem(k);
    } catch {}
  }, []);

  // ✅ make logout stable so it can be safely used in useEffect deps
  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    del(STORAGE_USER_KEY);
    del(STORAGE_TOKEN_KEY);
  }, [del]);

  /* Restore session if token exists */
  useEffect(() => {
    if (!user && token) {
      try {
        const raw = localStorage.getItem(STORAGE_USER_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && parsed.userType) {
            setUser(parsed);
          } else {
            logout();
          }
        } else {
          logout();
        }
      } catch {
        logout();
      }
    }
  }, [token, user, logout]);

  /* Clean invalid sessions */
  useEffect(() => {
    if (user && !user.userType) logout();
  }, [user, logout]);

  useEffect(() => {
    setIsInitializing(false);
  }, []);

  const handleApiError = (error) => {
    const msg = String(error?.message || "");
    if (msg.includes("Network") || msg.includes("fetch")) {
      return { success: false, error: "Network or CORS error." };
    }
    const mapped = mapOracleError(msg);
    return { success: false, error: mapped || msg || "Unexpected error." };
  };

  /* ===== LOGIN (POST) ===== */
  const login = async (
    email,
    password,
    selectedRole = null,
    isDemo = false,
    schoolId
  ) => {
    setIsLoading(true);
    try {
      const params = {
        email: String(email || ""),
        password: String(password || ""),
      };
      if (schoolId) params.p_school_id = String(schoolId);

      const url = buildApiUrl("staff/login/staff/", params);

      const response = await fetch(url, {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          password,
          p_school_id: schoolId ? String(schoolId) : undefined,
        }),
      });

      const { json, raw } = await parseMaybeJson(response);
      if (!response.ok) {
        const mapped = mapOracleError(JSON.stringify(json) || raw);
        setIsLoading(false);
        return { success: false, error: mapped || json?.message || "Login failed." };
      }

      const data = json || {};
      const ok =
        data.success === 1 ||
        data.success === "1" ||
        data.success === true ||
        data.success === "true";

      if (!ok || !data.user) {
        setIsLoading(false);
        return { success: false, error: data?.message || "Invalid credentials." };
      }

      /* Map role */
      const roleCode = String(data.user.role || "").toUpperCase();
      const roleMap = {
        TE: "teacher",
        AD: "admin",
        AC: "accountant",
        HT: "headteacher",
        SO: "owner",
      };
      const apiRole = roleMap[roleCode];

      if (!apiRole) {
        setIsLoading(false);
        return { success: false, error: "Unsupported login type." };
      }

      if (selectedRole && selectedRole !== apiRole && !isDemo) {
        setIsLoading(false);
        return {
          success: false,
          error: `Wrong role selected. You're registered as ${apiRole}.`,
        };
      }

      const finalRole = selectedRole || apiRole;

      const resolvedSchoolId =
        data?.user?.schoolId ?? data?.school?.id ?? (schoolId ? Number(schoolId) : null);

      const school = data.school || {};

      const userData = {
        id: data.user.id,
        email: data.user.email,
        fullName: data.user.fullName,
        name: data.user.fullName,
        userType: finalRole,
        apiRole: roleCode,
        originalRole: data.user.role,
        schoolId: resolvedSchoolId,
        plan: Number(school.package ?? data.user.plan ?? 1),
        package: Number(school.package ?? data.user.plan ?? 1),
        currency: school.currency || data.user.currency || "GHS",
        school,
        avatar: getAvatar(finalRole),
        permissions: getUserPermissions(data.user.role),
      };

      /* Save session */
      setUser(userData);
      save(STORAGE_USER_KEY, JSON.stringify(userData));

      if (data.token) {
        setToken(data.token);
        save(STORAGE_TOKEN_KEY, data.token);
      } else {
        setToken(null);
        del(STORAGE_TOKEN_KEY);
      }

      setIsLoading(false);
      return { success: true, user: userData };
    } catch (err) {
      setIsLoading(false);
      return handleApiError(err);
    }
  };

  /* ===== Generic API Call ===== */
  const apiCall = async (endpoint, options = {}) => {
    const { skipAuth = false, params, ...rest } = options;
    const url = buildApiUrl(endpoint, params);

    const headers = { Accept: "application/json", ...(rest.headers || {}) };
    if (!skipAuth && token) headers.Authorization = `Bearer ${token}`;

    const method = (rest.method || "GET").toUpperCase();
    const hasBody = rest.body !== undefined && rest.body !== null;
    if (hasBody && method !== "GET") {
      headers["Content-Type"] = headers["Content-Type"] || "application/json";
    } else {
      delete headers["Content-Type"];
    }

    try {
      const response = await fetch(url, { ...rest, method, headers });

      if (response.status === 401) {
        logout();
        return { success: false, error: "Session expired." };
      }

      const { json, raw } = await parseMaybeJson(response);

      if (!response.ok) {
        const mapped = mapOracleError(JSON.stringify(json) || raw);
        return { success: false, error: mapped || json?.message || "Request failed." };
      }

      return json ?? { success: true };
    } catch (err) {
      return handleApiError(err);
    }
  };

  const isAuthenticated = !!user && !!user.userType;

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        login,
        logout,
        apiCall,
        isAuthenticated,
        isLoading,
        isInitializing,
        API_BASE,
        buildApiUrl,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

/* ===== Permissions & Avatars ===== */
const getUserPermissions = (role) => {
  const p = {
    AD: ["manage_users", "view_reports", "system_settings", "manage_courses", "view_all_data"],
    TE: ["manage_classes", "grade_students", "view_student_data", "manage_assignments"],
    AC: ["manage_fees", "view_fees", "view_bills"],
    HT: ["manage_teachers", "monitor_students", "review_exams"],
    SO: ["manage_school", "access_all", "review_finances"],
  };
  return p[role] || [];
};

const getAvatar = (type) => {
  const a = {
    teacher: "👨‍🏫",
    admin: "👨‍💼",
    accountant: "💰",
    headteacher: "👩‍🏫",
    owner: "🏫",
  };
  return a[type] || "👤";
};
